import { nativeQueueDb, convertToQueueItem, type NativeQueueEntry } from './nativeQueueDb'
import type { Metadata, MetadataSummary, QueueItemT } from '@/lib/types/bgSyncTypes.ts'
import type { AxiosRequestConfig } from 'axios'
import {
  MAX_RETRIES,
  MAX_RETENTION_MS,
  MAX_QUEUE_SIZE,
  COMMENT_PREVIEW_LENGTH,
  BACKOFF_BASE_MS,
  BACKOFF_MAX_MS,
} from './syncConstants'
import { getApiBaseUrl } from '@/config/apiConfig'
import { safeRandomUUID } from '@/lib/helpers/safeRandomUUID.ts'

// Dev-only debug logging
const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[NativeQueue]', ...args)

type QueueUpdateListener = () => void
type FailureListener = (meta: Metadata) => void
// `responseBody` is the parsed JSON the server returned for the queued
// request. Plumbed through specifically so callers like the bulk-edit
// mutation can read per-id outcomes from `/issues/bulk` responses that
// HTTP-wise look 200 but semantically encode partial failures inside
// `results: [...]`. Undefined for media uploads, non-JSON responses, or
// when parsing fails.
type SuccessListener = (meta: Metadata, responseBody?: unknown) => void

// Outcome of replaying one queue entry, consumed by the fastQueue
// head-of-line gate. 'transient' (will retry) and 'deferred' (in backoff)
// block later same-requestUUID rows for the pass; 'synced' and 'permanent'
// let the lane continue.
type ProcessOutcome = 'synced' | 'transient' | 'permanent' | 'deferred'

class NativeQueueManager {
  private listeners: Set<QueueUpdateListener> = new Set()
  private failureListeners: Set<FailureListener> = new Set()
  private successListeners: Set<SuccessListener> = new Set()
  private fastSyncRunning = false
  private mediaSyncRunning = false
  private syncPromise: Promise<void> | null = null
  private maintenancePaused = false

  addListener(listener: QueueUpdateListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  addFailureListener(listener: FailureListener): () => void {
    this.failureListeners.add(listener)
    return () => this.failureListeners.delete(listener)
  }

  addSuccessListener(listener: SuccessListener): () => void {
    this.successListeners.add(listener)
    return () => this.successListeners.delete(listener)
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener()
    })
  }

  private notifyFailure(meta: Metadata): void {
    this.failureListeners.forEach(listener => {
      listener(meta)
    })
  }

  private notifySuccess(meta: Metadata, responseBody?: unknown): void {
    this.successListeners.forEach(listener => {
      listener(meta, responseBody)
    })
  }

  async enqueueRequest(
    config: AxiosRequestConfig,
    formData?: FormData,
  ): Promise<{ status: string; metadata: Metadata }> {
    // Check queue size to prevent unbounded growth
    const currentSize = await nativeQueueDb.requests.count()
    if (currentSize >= MAX_QUEUE_SIZE) {
      throw new Error(
        `Queue is full (${MAX_QUEUE_SIZE} items). Please sync pending items or check your connection.`,
      )
    }

    const url = config.url ?? ''
    const method = (config.method?.toUpperCase() ?? 'POST') as 'POST' | 'PATCH'
    const headers = (config.headers as Record<string, string>) ?? {}

    // Use API base URL for iOS native (capacitor://localhost won't work for fetch)
    const apiBaseUrl = getApiBaseUrl() || window.location.origin
    const path = new URL(url, apiBaseUrl).pathname
    const type = path.split('/').filter(Boolean).shift() ?? ''
    const isMedia = type === 'media_objects'

    let queueName: 'fastQueue' | 'mediaQueue'
    if (path.includes('/media_objects')) {
      queueName = 'mediaQueue'
    } else if (
      path.includes('/issues') ||
      path.includes('/comments') ||
      path.includes('/locations')
    ) {
      queueName = 'fastQueue'
    } else {
      throw new Error(`Unknown endpoint for queuing: ${path}`)
    }

    let data: Record<string, unknown> = {}
    let fileBlob: Blob | null = null
    let fileName: string | null = null

    if (isMedia && formData) {
      // Handle FormData properly - preserve duplicate keys as arrays
      for (const [key, value] of formData.entries()) {
        if (key === 'file') continue // Handle file separately
        if (data[key] !== undefined) {
          // Convert to array if duplicate key
          if (!Array.isArray(data[key])) {
            data[key] = [data[key]]
          }
          ;(data[key] as unknown[]).push(value)
        } else {
          data[key] = value
        }
      }
      // anno-trace: snapshot what the queue captured from FormData.
      // Inlined via JSON.stringify because Capacitor Android's logcat
      // bridge only relays the FIRST arg of console.*.
      console.info(
        '[anno-trace] nativeQueue captured formData ' +
          JSON.stringify({
            keys: Object.keys(data),
            annotationDataType: typeof data['annotationData'],
            annotationDataLength:
              typeof data['annotationData'] === 'string'
                ? (data['annotationData'] as string).length
                : null,
          }),
      )
      const file = formData.get('file')
      if (file instanceof Blob) {
        fileBlob = file
        fileName = file instanceof File ? file.name : 'file'
        debug(`File extracted from FormData: ${fileName}, size: ${file.size}, type: ${file.type}`)
      } else {
        debug(`WARNING: No file found in FormData, file value: ${typeof file}`)
      }
    } else if (config.data) {
      if (typeof config.data === 'string') {
        try {
          data = JSON.parse(config.data)
        } catch (error) {
          debug('Failed to parse request data:', error)
          throw new Error('Invalid request data format')
        }
      } else {
        data = config.data ?? {}
      }
    }

    const changedFields = Object.keys(data)
    // Serialized body for the PATCH dedup check below. Identical body = true
    // double-tap (dropped); a different value for the same field set is a
    // genuine new edit that must queue its own row so the latest value wins
    // on FIFO replay.
    const newBodyJson = JSON.stringify(data)

    const summary: MetadataSummary = {}
    if (changedFields.includes('labels')) summary.labels = data.labels as string[]
    if (changedFields.includes('status')) summary.status = data.status as string
    if (changedFields.includes('recipient')) summary.recipient = data.recipient as string
    if (changedFields.includes('location')) summary.location = data.location as string
    if (changedFields.includes('issue')) summary.issue = data.issue as string

    let name = isMedia ? fileName : (data.name as string | undefined)
    if (data.text) {
      const text = data.text as string
      name =
        text.substring(0, COMMENT_PREVIEW_LENGTH) +
        (text.length > COMMENT_PREVIEW_LENGTH ? '...' : '')
    }

    const metadata: Metadata = {
      method,
      type,
      changedFields,
      summary,
      // Native queue stores blobs separately, so we don't include file here
      // The name field contains the filename for media uploads
      name: name ?? '',
      mediaObjectUUID: isMedia ? ((data.mediaObjectUUID as string | null) ?? null) : null,
      requestUUID: (data.requestUUID as string) || safeRandomUUID(),
      fileUUID: isMedia ? ((data.fileUUID as string | null) ?? null) : null,
      fileCacheKey: null,
      failed: false,
      retries: 0,
      permanentFailure: false,
      error: '',
    }

    // Wrap dedup check + blob storage + request insert in a single Dexie transaction
    // to prevent TOCTOU race where two identical requests both pass the dedup check
    // before either is inserted (e.g., double-tap on mobile).
    let result: { status: string; metadata: Metadata } | null = null
    let queued = false

    // Convert blob to ArrayBuffer outside the transaction (async blob reads
    // can cause the IndexedDB transaction to auto-commit on iOS WebKit)
    let preparedBlob: Blob | null = null
    let blobCacheKey: string | null = null
    if (fileBlob) {
      blobCacheKey = `/native-file-cache/${metadata.fileUUID ?? safeRandomUUID()}`
      const arrayBuffer = await fileBlob.arrayBuffer()
      preparedBlob = new Blob([arrayBuffer], { type: fileBlob.type })
      debug(
        `Blob prepared: ${blobCacheKey}, original size: ${fileBlob.size}, converted size: ${preparedBlob.size}`,
      )
    } else if (isMedia) {
      debug(`WARNING: Media upload queued without file blob`)
    }

    try {
      await nativeQueueDb.transaction(
        'rw',
        nativeQueueDb.requests,
        nativeQueueDb.blobs,
        async () => {
          const existingEntries = await nativeQueueDb.requests
            .filter(e => {
              const m = e.metadata
              const basicMatch =
                m.method === metadata.method &&
                m.type === metadata.type &&
                m.requestUUID === metadata.requestUUID

              // For media uploads, also require fileUUID match (same file)
              if (metadata.fileUUID && m.fileUUID) {
                return basicMatch && m.fileUUID === metadata.fileUUID
              }

              // For PATCH (edits) the issue's requestUUID links all edits to
              // that issue, so dedup also requires the same changedFields AND
              // an identical serialized body. Identical body = true double-tap
              // (dropped). A DIFFERENT value for the same field set is a genuine
              // new edit that queues its own row, so the latest value wins when
              // fastQueue replays in FIFO order (latest-wins via ordered replay
              // — no coalescing/revision guard; see lib/AGENTS.md +
              // audit/domains/offline.md).
              if (metadata.method === 'PATCH' && m.changedFields && metadata.changedFields) {
                const sameFields =
                  m.changedFields.length === metadata.changedFields.length &&
                  m.changedFields.every(f => metadata.changedFields!.includes(f))
                return basicMatch && sameFields && e.body === newBodyJson
              }

              return basicMatch
            })
            .toArray()

          if (existingEntries.length > 0) {
            debug(`Duplicate entry ignored (${metadata.requestUUID})`)
            result = { status: 'duplicate', metadata }
            return // Abort rest of transaction body (Dexie still commits)
          }

          if (preparedBlob && blobCacheKey) {
            await nativeQueueDb.blobs.put({ id: blobCacheKey, blob: preparedBlob })

            // Verify blob was stored correctly
            const storedBlob = await nativeQueueDb.blobs.get(blobCacheKey)
            if (!storedBlob?.blob || storedBlob.blob.size === 0) {
              throw new Error(
                `Failed to store blob: verification failed, size: ${storedBlob?.blob?.size ?? 0}`,
              )
            }
            debug(`Blob stored and verified: ${blobCacheKey}, size: ${storedBlob.blob.size}`)

            metadata.fileCacheKey = blobCacheKey
          }

          const bodyToStore = isMedia
            ? JSON.stringify({ ...data, file: undefined, fileCacheKey: metadata.fileCacheKey })
            : JSON.stringify(data)

          const entry: NativeQueueEntry = {
            queueName,
            url: new URL(url, apiBaseUrl).href,
            method,
            headers: { ...headers },
            body: bodyToStore,
            timestamp: Date.now(),
            metadata,
          }

          await nativeQueueDb.requests.add(entry)

          debug(
            `Request queued to ${queueName}: ${metadata.requestUUID}, fileUUID: ${metadata.fileUUID}, cacheKey: ${metadata.fileCacheKey}`,
          )

          result = { status: 'queued', metadata }
          queued = true
        },
      )

      if (queued) {
        this.notifyListeners()
      }

      return result!
    } catch (error) {
      console.error('[NativeQueue] Failed to queue request:', error)
      throw new Error(
        `Failed to queue request: ${error instanceof Error ? error.message : 'IndexedDB error'}`,
      )
    }
  }

  async getAllEntries(): Promise<QueueItemT[]> {
    const entries = await nativeQueueDb.requests.toArray()
    return entries.map(e => convertToQueueItem(e))
  }

  async deleteEntry(id: number): Promise<void> {
    const entry = await nativeQueueDb.requests.get(id)
    if (entry?.metadata.fileCacheKey) {
      await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
    }
    await nativeQueueDb.requests.delete(id)
    debug(`Deleted entry ${id}`)
    this.notifyListeners()
  }

  async deleteAllFailed(): Promise<number[]> {
    const entries = await nativeQueueDb.requests
      .filter(e => Boolean(e.metadata.failed) || Boolean(e.metadata.permanentFailure))
      .toArray()

    const deletedIds: number[] = []
    for (const entry of entries) {
      if (entry.id !== undefined) {
        if (entry.metadata.fileCacheKey) {
          await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
        }
        await nativeQueueDb.requests.delete(entry.id)
        deletedIds.push(entry.id)
      }
    }

    debug(`Deleted ${deletedIds.length} failed entries`)
    this.notifyListeners()

    return deletedIds
  }

  /**
   * Metadata reset applied when a failed entry is re-queued for retry. Besides
   * the failure flags it MUST clear:
   *   - `nextRetryAt` — doPerformSync skips entries whose backoff hasn't elapsed
   *     (`if (nextRetryAt && Date.now() < nextRetryAt) return`), so a retry that
   *     left it set would be silently backoff-blocked until the window passed.
   *   - `status` / `errorDetail` — the structured failure fields the failure
   *     listeners surface as UX; leaving them stale would mislabel the retry.
   * Single source of truth so retryEntry + retryAllFailed stay identical.
   */
  private clearFailureState(meta: Metadata): Metadata {
    return {
      ...meta,
      failed: false,
      permanentFailure: false,
      retries: 0,
      error: '',
      status: undefined,
      errorDetail: undefined,
      nextRetryAt: undefined,
    }
  }

  async retryEntry(id: number): Promise<void> {
    const entry = await nativeQueueDb.requests.get(id)
    if (!entry) return

    await nativeQueueDb.requests.update(id, {
      metadata: this.clearFailureState(entry.metadata),
      timestamp: Date.now(),
    })

    this.notifyListeners()
    await this.performSync()
  }

  /**
   * Reset ALL failed / permanently-failed entries and re-sync. Backs the manual
   * "sync now" button so a user can replay everything that's stuck — including
   * permanent failures — in one action. Mirrors `deleteAllFailed`'s filter.
   */
  async retryAllFailed(): Promise<void> {
    const entries = await nativeQueueDb.requests
      .filter(e => Boolean(e.metadata.failed) || Boolean(e.metadata.permanentFailure))
      .toArray()

    let changed = 0
    for (const entry of entries) {
      if (entry.id === undefined) continue
      await nativeQueueDb.requests.update(entry.id, {
        metadata: this.clearFailureState(entry.metadata),
        timestamp: Date.now(),
      })
      changed++
    }

    if (changed > 0) this.notifyListeners()
    await this.performSync()
  }

  async performSync(): Promise<void> {
    // Mutex: if sync is already running, wait for it instead of starting another
    if (this.syncPromise) {
      debug('Sync already in progress, waiting...')
      return this.syncPromise
    }

    this.syncPromise = this.doPerformSync()
    try {
      await this.syncPromise
    } finally {
      this.syncPromise = null
    }
  }

  /**
   * Pause/resume queue draining for a maintenance window. While paused,
   * doPerformSync() early-returns so locked-out (non-team) users don't burn the
   * 3-retry budget against maintenance 503s. Resuming triggers an immediate
   * drain. Driven by useMaintenanceStatus from GlobalLayout.
   */
  setMaintenancePaused(paused: boolean): void {
    if (this.maintenancePaused === paused) return
    this.maintenancePaused = paused
    if (!paused) {
      void this.performSync()
    }
  }

  private async doPerformSync(): Promise<void> {
    debug('performSync started')

    if (this.maintenancePaused) {
      debug('Maintenance mode active — skipping queue sync')
      return
    }

    // Clean up stale entries (older than 30 days) before syncing
    await this.cleanupStaleEntries()

    const fastEntries = await nativeQueueDb.requests
      .where('queueName')
      .equals('fastQueue')
      .toArray()

    const mediaEntries = await nativeQueueDb.requests
      .where('queueName')
      .equals('mediaQueue')
      .toArray()

    // Drain the fast queue (issue/comment creates) BEFORE the media queue.
    // A queued offline "create issue + photo" enqueues `POST /issues` (fast)
    // and `POST /media_objects` (media) sharing one requestUUID. The backend
    // binds them by requestUUID in BOTH directions (the media POST attaches to
    // an existing issue; the issue POST reverse-attaches pre-existing media —
    // AttachmentsBindStateProcessor), so SEQUENTIAL replay in either order links
    // correctly. The hazard is CONCURRENCY: draining both queues at once put
    // both POSTs in flight together, and if each request's bind-lookup runs
    // before the other has committed, neither sees the other → the photo
    // persists ORPHANED (permanent — both got 2xx, no retry) and the issue
    // reopens with no attachment. Serialising fast→media closes that window.
    await this.processFastQueue(fastEntries.filter(e => !e.metadata.permanentFailure))

    // Hold back any media upload whose same-requestUUID fast create is still
    // pending (failed / backed-off this pass): uploading it now would commit a
    // media_object before its issue/comment exists. It stays queued and replays
    // on a later pass, after the create finally lands.
    const pendingFastCreates = await this.getPendingFastCreateRequestUUIDs()
    await this.processMediaQueue(
      mediaEntries.filter(
        e => !e.metadata.permanentFailure && !pendingFastCreates.has(e.metadata.requestUUID),
      ),
    )
  }

  /**
   * requestUUIDs of fast-queue CREATE (POST) rows still present after a fast
   * drain pass — i.e. creates that did not succeed (a 2xx deletes the row).
   * Their same-requestUUID media uploads must wait so they can't be persisted
   * before the parent issue/comment exists. PATCH edits are excluded: their
   * media (if any) links via owningIssue, not a pending create's requestUUID.
   */
  private async getPendingFastCreateRequestUUIDs(): Promise<Set<string>> {
    const entries = await nativeQueueDb.requests.where('queueName').equals('fastQueue').toArray()
    return new Set(
      entries
        .filter(entry => entry.metadata.method === 'POST')
        .map(entry => entry.metadata.requestUUID),
    )
  }

  /**
   * Remove entries older than MAX_RETENTION_MS (30 days) to prevent unbounded growth
   */
  private async cleanupStaleEntries(): Promise<void> {
    const cutoff = Date.now() - MAX_RETENTION_MS

    const staleEntries = await nativeQueueDb.requests.filter(e => e.timestamp < cutoff).toArray()

    if (staleEntries.length === 0) return

    debug(`Cleaning up ${staleEntries.length} stale entries`)

    for (const entry of staleEntries) {
      if (entry.id !== undefined) {
        // Clean up associated blob if exists
        if (entry.metadata.fileCacheKey) {
          await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
        }
        await nativeQueueDb.requests.delete(entry.id)
      }
    }

    this.notifyListeners()
  }

  private async processFastQueue(entries: NativeQueueEntry[]): Promise<void> {
    if (this.fastSyncRunning || entries.length === 0) return
    this.fastSyncRunning = true

    // Drain strictly FIFO by insertion order (id), with per-requestUUID
    // head-of-line ordering: if an earlier row for a requestUUID did not fully
    // succeed (transient failure, or pending backoff), block its later
    // same-requestUUID rows for THIS pass so a newer edit cannot apply before
    // the earlier one and let a stale value win. A permanently-failed head
    // releases the lane (its later edits are the user's newer intent).
    // Different requestUUIDs drain independently. fastQueue MUST stay
    // serial-FIFO — this ordering is the correctness guarantee behind
    // latest-wins (see lib/AGENTS.md).
    const ordered = [...entries].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
    const blocked = new Set<string>()

    try {
      for (const entry of ordered) {
        const uuid = entry.metadata.requestUUID
        if (blocked.has(uuid)) {
          debug(`head-of-line: deferring ${uuid} behind an unfinished earlier edit`)
          continue
        }
        const outcome = await this.processEntry(entry)
        if (outcome === 'transient' || outcome === 'deferred') {
          blocked.add(uuid)
        }
      }
    } finally {
      this.fastSyncRunning = false
    }
  }

  private async processMediaQueue(entries: NativeQueueEntry[]): Promise<void> {
    if (this.mediaSyncRunning || entries.length === 0) return
    this.mediaSyncRunning = true

    try {
      for (const entry of entries) {
        await this.processEntry(entry)
      }
    } finally {
      this.mediaSyncRunning = false
    }
  }

  private async processEntry(entry: NativeQueueEntry): Promise<ProcessOutcome> {
    if (entry.id === undefined) return 'synced'

    const meta: Metadata = { ...entry.metadata }

    // Skip if entry has a future retry time (exponential backoff)
    const nextRetryAt = meta.nextRetryAt as number | undefined
    if (nextRetryAt && Date.now() < nextRetryAt) {
      debug(
        `Skipping ${meta.requestUUID}, retry scheduled for ${new Date(nextRetryAt).toISOString()}`,
      )
      return 'deferred'
    }

    try {
      let requestInit: RequestInit = {
        method: entry.method,
        headers: { ...entry.headers, 'X-Native-Queue-Replay': 'true' },
        credentials: 'include',
      }

      if (meta.type === 'media_objects' && meta.fileCacheKey) {
        const cachedBlob = await nativeQueueDb.blobs.get(meta.fileCacheKey)

        // CRITICAL: If blob cannot be retrieved, fail the entry - don't send without file
        if (!cachedBlob?.blob) {
          debug(
            `Blob not found for ${meta.requestUUID}, fileUUID: ${meta.fileUUID}, cacheKey: ${meta.fileCacheKey}`,
          )
          meta.failed = true
          meta.permanentFailure = true
          meta.error = 'File data lost - please try uploading again'

          await nativeQueueDb.requests.update(entry.id, {
            metadata: meta,
            timestamp: Date.now(),
          })
          this.notifyFailure(meta)
          this.notifyListeners()
          return 'permanent'
        }

        debug(`Blob found for ${meta.requestUUID}, size: ${cachedBlob.blob.size}`)

        const formData = new FormData()
        formData.append('file', cachedBlob.blob, meta.name || 'file')

        let bodyData: Record<string, unknown> = {}
        if (entry.body) {
          try {
            bodyData = JSON.parse(entry.body)
          } catch {
            debug('Failed to parse entry body for', meta.requestUUID)
          }
        }
        Object.entries(bodyData).forEach(([key, value]) => {
          if (key !== 'file' && key !== 'fileCacheKey' && value !== undefined) {
            formData.append(key, String(value))
          }
        })
        // anno-trace: snapshot what the replay reassembled before the
        // POST goes out. If annotationData is in the keys here but the
        // server-side row doesn't store it, the bug is upstream of the
        // queue. If it's missing here but present at enqueue time, the
        // bug is in the JSON.stringify / parse round-trip.
        console.info(
          '[anno-trace] nativeQueue replay rebuilt formData ' +
            JSON.stringify({
              keys: Object.keys(bodyData),
              annotationDataType: typeof bodyData['annotationData'],
              annotationDataLength:
                typeof bodyData['annotationData'] === 'string'
                  ? (bodyData['annotationData'] as string).length
                  : null,
            }),
        )

        const newHeaders: Record<string, string> = {
          ...entry.headers,
          'X-Native-Queue-Replay': 'true',
        }
        delete newHeaders['content-type']
        delete newHeaders['Content-Type']

        requestInit = {
          method: entry.method,
          headers: newHeaders,
          body: formData,
          credentials: 'include',
        }
      } else if (entry.body) {
        requestInit.body = entry.body
      }

      const response = await fetch(entry.url, requestInit)

      // Maintenance window: re-queue WITHOUT consuming the retry budget and
      // WITHOUT touching `timestamp` (so 7-day retention still measures from the
      // original enqueue). Return 'deferred' so the fastQueue head-of-line gate
      // stops the pass here — a later same-requestUUID edit must not apply ahead
      // of this one. The pause flag normally prevents reaching this; it's the
      // safety net for a sync already in flight when the window opens.
      if (response.status === 503 && response.headers.get('x-maintenance-mode') === 'true') {
        meta.nextRetryAt = Date.now() + 60_000
        await nativeQueueDb.requests.update(entry.id, { metadata: meta })
        this.notifyListeners()
        debug(`Maintenance 503 for ${meta.requestUUID} — deferred without counting retry`)
        return 'deferred'
      }

      if (!response.ok) {
        meta.retries = (meta.retries ?? 0) + 1
        meta.failed = true
        meta.error = `HTTP ${response.status}: ${response.statusText}`
        // Surface the HTTP status as a structured field so failure
        // listeners can map it to friendlier UX (403 → permission denied,
        // 5xx → server error) without parsing the human string.
        meta.status = response.status
        // Pull a server-provided detail out of the response body when
        // available — JSON-LD/Hydra exposes it under `hydra:description`
        // or `detail`. Cloned because we already consumed the response
        // status above and may read the body again here. Best-effort:
        // failures to parse silently fall back to the generic message.
        try {
          const cloned = response.clone()
          const ct = cloned.headers.get('content-type') ?? ''
          if (ct.includes('json')) {
            const body = (await cloned.json()) as Record<string, unknown> | null
            const detail =
              (body?.['hydra:description'] as string | undefined) ??
              (body?.['detail'] as string | undefined) ??
              (body?.['description'] as string | undefined) ??
              (body?.['title'] as string | undefined)
            if (typeof detail === 'string' && detail.length > 0) {
              meta.errorDetail = detail
            }
          }
        } catch {
          // body unreadable — leave errorDetail unset
        }

        const isClientErr = response.status >= 400 && response.status < 500
        if (isClientErr || meta.retries >= MAX_RETRIES) {
          meta.permanentFailure = true
          this.notifyFailure(meta)
          // Keep blobs on permanent failure so user can retry after resetting.
          // Blobs are cleaned up in deleteEntry(), deleteAllFailed(), and cleanupStaleEntries().
        } else {
          // Calculate exponential backoff for next retry
          const backoffMs = Math.min(
            BACKOFF_BASE_MS * Math.pow(2, meta.retries - 1),
            BACKOFF_MAX_MS,
          )
          meta.nextRetryAt = Date.now() + backoffMs
          debug(`Retry ${meta.retries} for ${meta.requestUUID}, next attempt in ${backoffMs}ms`)
        }

        await nativeQueueDb.requests.update(entry.id, {
          metadata: meta,
          timestamp: Date.now(),
        })
        this.notifyListeners()
        return meta.permanentFailure ? 'permanent' : 'transient'
      }

      // Parse JSON response bodies before deleting the entry so success
      // listeners can read endpoint-specific payloads (e.g. per-id
      // outcomes from /issues/bulk). Cheap on the typical small-issue
      // response; silently skipped for non-JSON or parse errors.
      let responseBody: unknown = undefined
      const contentType = response.headers.get('content-type') ?? ''
      if (contentType.includes('json')) {
        try {
          responseBody = await response.json()
        } catch {
          // body unreadable or empty — leave responseBody undefined
        }
      }

      if (meta.fileCacheKey) {
        await nativeQueueDb.blobs.delete(meta.fileCacheKey)
      }
      await nativeQueueDb.requests.delete(entry.id)
      debug(`Successfully synced ${meta.requestUUID}`)
      this.notifySuccess(meta, responseBody)
      this.notifyListeners()
      return 'synced'
    } catch (err) {
      meta.failed = true
      meta.retries = (meta.retries ?? 0) + 1
      meta.error = err instanceof Error ? err.message : 'Unknown error'

      if (meta.retries >= MAX_RETRIES) {
        meta.permanentFailure = true
        this.notifyFailure(meta)
        // Keep blobs on permanent failure so user can retry after resetting.
        // Blobs are cleaned up in deleteEntry(), deleteAllFailed(), and cleanupStaleEntries().
      } else {
        // Calculate exponential backoff for next retry
        const backoffMs = Math.min(BACKOFF_BASE_MS * Math.pow(2, meta.retries - 1), BACKOFF_MAX_MS)
        meta.nextRetryAt = Date.now() + backoffMs
        debug(`Retry ${meta.retries} for ${meta.requestUUID}, next attempt in ${backoffMs}ms`)
      }

      await nativeQueueDb.requests.update(entry.id, {
        metadata: meta,
        timestamp: Date.now(),
      })
      this.notifyListeners()
      return meta.permanentFailure ? 'permanent' : 'transient'
    }
  }
}

export const nativeQueueManager = new NativeQueueManager()
